import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import ReCaptcha from '@/components/ui/ReCaptcha'
import LocationSelect from '@/components/forms/LocationSelect'
import AuthFooter from '@/components/layout/AuthFooter'
import { validateEmail, validatePassword } from '@/utils/validation'
import { LANGUAGES } from '@/utils/constants'

export default function Register() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone_number: '',
    profession: '',
    certifications: '',
    continent: '',
    country: '',
    state: '',
    city: '',
    language: 'English',
  })
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const { register } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    // Clear error for this field
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' })
    }
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required'
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required'

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    const passwordValidation = validatePassword(formData.password)
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0]
    }

    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match'
    }

    if (!termsAccepted) {
      newErrors.terms = 'You must accept the Terms of Use to register'
    }

    if (!privacyAccepted) {
      newErrors.privacy = 'You must accept the Privacy Policy to register'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    setIsLoading(true)

    try {
      const { confirm_password, ...registerData } = formData
      // Pass recaptcha token and legal acceptances to register
      await register({
        ...registerData,
        terms_accepted: termsAccepted,
        privacy_accepted: privacyAccepted,
        terms_version: '1.0',
        privacy_version: '1.0',
      }, recaptchaToken)
      navigate('/dashboard')
    } catch (err: any) {
      // Check if it's a maintenance mode error
      if (err.response?.status === 503) {
        const maintenanceMsg = err.response?.data?.errors?.maintenance_message || err.response?.data?.message
        const endTime = err.response?.data?.errors?.end_datetime

        let displayMsg = maintenanceMsg || 'System is currently under maintenance'
        if (endTime) {
          displayMsg += `\n\nExpected to resume: ${new Date(endTime).toLocaleString()}`
        }
        setErrors({ general: displayMsg })
      } else {
        setErrors({ general: err.response?.data?.message || err.message || 'Registration failed. Please try again.' })
      }
      // Reset reCAPTCHA on error
      setRecaptchaToken(null)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-primary-600 mb-2">BarterSheet</h1>
            <p className="text-gray-600">Create your account</p>
          </div>

          {/* Error Alert */}
          {errors.general && (
            <div className="mb-6">
              <Alert type="error" message={errors.general} onClose={() => setErrors({ ...errors, general: '' })} />
            </div>
          )}

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  className="input"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
                {errors.first_name && <p className="text-sm text-red-600 mt-1">{errors.first_name}</p>}
              </div>

              <div>
                <label className="label">Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  className="input"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
                {errors.last_name && <p className="text-sm text-red-600 mt-1">{errors.last_name}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="label">Email Address *</label>
              <input
                type="email"
                name="email"
                className="input"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Password *</label>
                <input
                  type="password"
                  name="password"
                  className="input"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                {errors.password && <p className="text-sm text-red-600 mt-1">{errors.password}</p>}
              </div>

              <div>
                <label className="label">Confirm Password *</label>
                <input
                  type="password"
                  name="confirm_password"
                  className="input"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                />
                {errors.confirm_password && <p className="text-sm text-red-600 mt-1">{errors.confirm_password}</p>}
              </div>
            </div>

            {/* Contact and Professional Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Phone Number</label>
                <input
                  type="tel"
                  name="phone_number"
                  className="input"
                  value={formData.phone_number}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="label">Language</label>
                <select
                  name="language"
                  className="select"
                  value={formData.language}
                  onChange={handleChange}
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="label">Profession</label>
              <input
                type="text"
                name="profession"
                className="input"
                value={formData.profession}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="label">Certifications</label>
              <textarea
                name="certifications"
                className="textarea"
                rows={3}
                value={formData.certifications}
                onChange={handleChange}
              />
            </div>

            {/* Location */}
            <LocationSelect
              continent={formData.continent}
              country={formData.country}
              state={formData.state}
              city={formData.city}
              onContinentChange={(value) =>
                setFormData({ ...formData, continent: value, country: '', state: '', city: '' })
              }
              onCountryChange={(value) =>
                setFormData({ ...formData, country: value, state: '', city: '' })
              }
              onStateChange={(value) =>
                setFormData({ ...formData, state: value, city: '' })
              }
              onCityChange={(value) => setFormData({ ...formData, city: value })}
            />

            {/* reCAPTCHA */}
            <ReCaptcha
              onChange={(token) => setRecaptchaToken(token)}
              onError={() => setErrors({ general: 'reCAPTCHA verification failed. Please try again.' })}
            />

            {/* Legal Agreements */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked)
                    if (errors.terms) setErrors({ ...errors, terms: '' })
                  }}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="terms" className="ml-2 text-sm text-gray-700">
                  I have read and agree to the{' '}
                  <Link
                    to="/legal/terms-of-use"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 font-medium underline"
                  >
                    Terms of Use
                  </Link>
                  <span className="text-red-600"> *</span>
                </label>
              </div>
              {errors.terms && <p className="text-sm text-red-600 ml-6">{errors.terms}</p>}

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={privacyAccepted}
                  onChange={(e) => {
                    setPrivacyAccepted(e.target.checked)
                    if (errors.privacy) setErrors({ ...errors, privacy: '' })
                  }}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="privacy" className="ml-2 text-sm text-gray-700">
                  I have read and agree to the{' '}
                  <Link
                    to="/legal/privacy-policy"
                    target="_blank"
                    className="text-primary-600 hover:text-primary-500 font-medium underline"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-red-600"> *</span>
                </label>
              </div>
              {errors.privacy && <p className="text-sm text-red-600 ml-6">{errors.privacy}</p>}
            </div>

            <Button type="submit" className="w-full" isLoading={isLoading} disabled={!termsAccepted || !privacyAccepted}>
              Create Account
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <AuthFooter />
      </div>
    </div>
  )
}
